import type { ServerLogLevel } from '@/shared/types/api';

/**
 * How far before a run's client-side start a Logs link begins. The browser clock can be ahead of
 * the server's, and the server never returns a line older than `from`, so without this margin a
 * run's first lines (e.g. feeds that fail fast) would be hidden.
 */
export const CLOCK_SKEW_MS = 60_000;

/** A request's lines sit within minutes of any one of them; the longest runs (all feeds) take ~15 min. */
export const TRACE_LOOKBEHIND_MS = 15 * 60_000;

/** Client-side start of a manual run, for a Logs link; early by {@link CLOCK_SKEW_MS}. */
export const runStartedAt = () => new Date(Date.now() - CLOCK_SKEW_MS).toISOString();

export interface LogsLinkOptions {
  traceId?: string;
  /** ISO start of the window to show. */
  from?: string;
  minLevel?: ServerLogLevel;
}

/**
 * Deep link into Admin → Logs. With a trace id the level defaults to Verbose, so every line of
 * that request shows (the per-feed errors sit between Information request lines); without one
 * it defaults to the tab's own Warning default. With a trace id, `from` only bounds the scan (the
 * trace filter does the narrowing), so it is moved back by {@link TRACE_LOOKBEHIND_MS}: a client
 * clock far ahead of the server's cannot hide the start of the request.
 */
export function buildLogsHref({ traceId, from, minLevel }: LogsLinkOptions = {}): string {
  const params = new URLSearchParams({ tab: 'logs' });
  if (traceId) params.set('traceId', traceId);
  if (from) params.set('from', traceId ? lookBehind(from) : from);
  params.set('minLevel', minLevel ?? (traceId ? 'Verbose' : 'Warning'));
  return `/admin?${params.toString()}`;
}

function lookBehind(from: string): string {
  const at = Date.parse(from);
  return Number.isFinite(at) ? new Date(at - TRACE_LOOKBEHIND_MS).toISOString() : from;
}
